import { StatsigClient } from '@statsig/js-client';
import {
  differenceInDays,
  differenceInMilliseconds,
  getDate,
  getMonth,
  getYear,
} from 'date-fns';
import mixpanel, { type Dict } from 'mixpanel-browser';
import {
  type DayOfWeek,
  DeliveryDayOfWeekKorean,
} from '../../api/subscription/dto/get-my-subscriptions.dto';
import { ConversionFlag } from '../../app/gift/subscription/hooks/useConversionSubscriptionGift';
import {
  type Product,
  ProductSizeKorean,
  ProductType,
  ProductTypeKorean,
} from '../../type';
import BrazeSDK from '../common/external-library/braze-sdk';
import { APP_STATSIG } from '../common/next-migration';
import { SESSION_STORAGE_KEYS } from '../common/session-storage-keys';
import { isReactNative } from '../react-native';
import { BOX_TYPE_LABEL, type BoxTypeLabel } from './box-type-label';

import type { SharedGtmEventProperties } from './shared-gtm-event-properties';

/**
 * 이벤트 이름에 맞는 속성 타입.
 *
 * 두 곳 이상에서 선언된 이벤트(`SharedGtmEventProperties`에 등록된 것)는 계약에 적힌 속성의 형식을
 * 강제하고, 그 외 이벤트는 지금처럼 자유롭게 둔다. `Dict`와 교차시키는 이유는 도메인마다 자기만
 * 보내는 속성이 있기 때문 — 계약에 적은 속성만 검사하고 나머지는 그대로 통과시킨다.
 */
type GtmTrackProperties<EventName extends string> = Dict &
  (EventName extends keyof SharedGtmEventProperties
    ? SharedGtmEventProperties[EventName]
    : unknown);

export enum GtmLoginMethod {
  kakao = 'kakao',
  apple = 'apple',
  email = 'email',
}

/** 믹스패널 상품 이름은 '박스'를 뺀 짧은 이름을 쓴다 */
const PRODUCT_TYPE_SHORT_NAME = {
  [ProductType.SUBSCRIBE]: '집밥',
  [ProductType.SALAD]: '샐러드',
  [ProductType.BABY_FOOD]: '이유식',
} as const;

export type BuynowProductType = BoxTypeLabel | 'gift_salad' | 'gift_default';

export interface EventProduct {
  productId: number;
  /**
   * @example 스탠다드박스 - 샐러드, 집밥박스 - 점보
   * @link https://uglyushq.slack.com/archives/C04MFN7DMAN/p1730378460430089?thread_ts=1730369409.834689&cid=C04MFN7DMAN
   */
  productName: string;
}

export class Gtm {
  statsigClient: StatsigClient | undefined;

  constructor() {
    // 클라이언트 환경에서만 StatsigClient.instance() 호출
    if (typeof window !== 'undefined') {
      this.statsigClient = StatsigClient.instance(APP_STATSIG ?? '');
    }
  }

  // 이벤트 전송
  track<EventName extends string>(
    event_name: EventName,
    // 계약에 등록된 이벤트는 속성 인자를 생략할 수 없다 — 생략을 허용하면
    // 필수 속성(coupon_id 등)이 빠진 채로 전송되는 것을 막지 못한다.
    ...args: EventName extends keyof SharedGtmEventProperties
      ? [properties: GtmTrackProperties<EventName>]
      : [properties?: Dict]
  ) {
    const [properties] = args;

    const commonProperties = {
      ...(properties ? properties : {}),
      platform: isReactNative() ? 'app' : 'web',
    };

    try {
      this.statsigClient?.logEvent(event_name, '', commonProperties);
    } catch (e) {
      console.error('Statsig track error: ', e);
    }

    try {
      mixpanel.track(event_name, commonProperties);
    } catch (e) {
      console.error('Mixpanel track error: ', e);
    }

    try {
      const utmParams = this.getUtmParameters();

      BrazeSDK.logCustomEvent(event_name, {
        ...commonProperties,
        ...utmParams,
      });
    } catch (e) {
      console.error('Braze track error: ', e);
    }
  }

  // Date 를 받아 year, month, day 로 분리
  protected separateDate(date: Date) {
    const year = getYear(date);
    const month = getMonth(date) + 1;
    const day = getDate(date);

    return { year, month, day };
  }

  protected getTimeUnits<
    T extends {
      deliveryDate: string | Date;
      year: number;
      week: number;
      day: DayOfWeek;
    },
  >(time: T) {
    return {
      year: time.year,
      month: getMonth(new Date(time.deliveryDate)) + 1,
      week: time.week,
      day: DeliveryDayOfWeekKorean[time.day],
    };
  }

  getEventProduct = ({
    id,
    productType,
    productSize,
  }: Pick<Product, 'id' | 'productType' | 'productSize'>): EventProduct => {
    return {
      productId: id,
      /**
       * @link https://uglyushq.slack.com/archives/C02N55A3FM5/p1730771573540109?thread_ts=1730770049.514139&cid=C02N55A3FM5
       * 샐러드박스-스탠다드
       * 집밥박스-스탠다드
       * 집박박스-점보
       */
      productName: `${ProductTypeKorean[productType]}-${ProductSizeKorean[productSize]}`,
    };
  };

  /** 믹스패널 상품 정보 반환 */
  getEventProductV2 = ({
    id,
    productType,
    productSize,
    conversionFlag,
  }: Pick<Product, 'id' | 'productSize' | 'productType'> & {
    conversionFlag: ConversionFlag;
  }): {
    product_id: string;
    product_name: string;
    product_type: BuynowProductType;
  } => {
    const product_type = this.getBuynowProductType({
      productType,
      conversionFlag,
    });

    return {
      product_id: id?.toString() ?? '',
      product_name: `${PRODUCT_TYPE_SHORT_NAME[productType]}-${ProductSizeKorean[productSize]}`,
      product_type: product_type,
    };
  };

  /**
   * 경로에서 숫자로만 이루어진 세그먼트를 필터링하여 정규화된 경로를 반환합니다.
   * @param path - 정규화할 경로
   * @returns 정규화된 경로
   */
  getNormalizedPath = (path: string): string => {
    // 경로를 '/' 기준으로 분리
    const segments = path.split('/').filter(Boolean);

    // 숫자로만 이루어진 세그먼트를 필터링
    const filteredSegments = segments.filter(
      (segment) => !/^\d+$/.test(segment),
    );

    // 필터링된 세그먼트를 다시 '/'로 연결
    return '/' + filteredSegments.join('/');
  };

  /** 현재 경로 반환 */
  get path() {
    return typeof window !== 'undefined'
      ? this.getNormalizedPath(window.location.pathname)
      : null;
  }

  /** 이전 경로 반환 */
  get previousPath() {
    const previousPath =
      typeof window !== 'undefined'
        ? window.sessionStorage.getItem(SESSION_STORAGE_KEYS.PREVIOUS_PATH)
        : null;

    return previousPath ? this.getNormalizedPath(previousPath) : null;
  }

  /** 정기배송 상품 타입 반환 */
  getBuynowProductType(props: {
    productType: ProductType;
    conversionFlag: ConversionFlag;
  }): BuynowProductType {
    const { productType, conversionFlag } = props;

    const isGiftConversion = conversionFlag === ConversionFlag.ONGOING;

    if (isGiftConversion) {
      // 이유식은 선물로 받을 수 없어(서버가 수락을 막는다) 채소박스 두 종류만 온다
      return productType === ProductType.SALAD ? 'gift_salad' : 'gift_default';
    }

    return BOX_TYPE_LABEL[productType];
  }

  getRestDays(targetDate: string | Date): number {
    try {
      const restDay = differenceInDays(new Date(targetDate), new Date());

      // restDay가 0을 초과하면 restDay를 보여준다.
      if (restDay > 0) return restDay + 1;

      // 23:59:59 ~ 00:00:01 의 경우 restTime이 0보다 큼
      const restTime = differenceInMilliseconds(
        new Date(targetDate),
        new Date(),
      );

      // restTime이 0보다 큰 경우에는 1일 남았다고 보여준다
      return restTime >= 0 ? 1 : 0;
    } catch (e) {
      console.error('getRestDays error: ', e);
      return 0;
    }
  }

  // UTM 파라미터 추출
  private getUtmParameters(): Record<string, string> {
    if (typeof window === 'undefined') return {};

    const urlParams = new URLSearchParams(window.location.search);
    const utmParams: Record<string, string> = {};

    // UTM 파라미터들 추출
    const utmKeys = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'utm_content',
    ];

    utmKeys.forEach((key) => {
      const value = urlParams.get(key);
      if (value) {
        utmParams[key] = value;
      }
    });

    return utmParams;
  }
}
